import React, { useEffect } from 'react'
import { Prompt } from 'react-router-dom'
import { Formik, FormikHelpers, FormikValues, useFormikContext } from 'formik'
import { AnyObjectSchema } from 'yup'
import Loading from '../../shared/loading'
import { useCatalogue } from '../CatalogueProvider'
import { SaveOutcome } from '../catalogueTypes'
import { categoryIdFromPath, panelKey, parseWorkspace } from '../catalogueUrls'
import { t } from '../../../i18n'

const UnsavedChangesGuard = () => {
  const { dirty, isSubmitting } = useFormikContext<FormikValues>()
  const { state } = useCatalogue()
  const { categoryId, panelKey: currentPanelKey } = state
  const active = dirty && !isSubmitting

  useEffect(() => {
    if (!active) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [active])

  return (
    <Prompt
      when={active}
      message={(location) => {
        const staysOnPanel =
          categoryIdFromPath(location.pathname) === categoryId &&
          panelKey(parseWorkspace(location.search).panel) === currentPanelKey
        return staysOnPanel ? true : t('catalogue.unsaved_changes')
      }}
    />
  )
}

const SubmittingIndicator = () => {
  const { isSubmitting } = useFormikContext<FormikValues>()
  return isSubmitting ? <Loading /> : null
}

interface CatalogueItemFormProps<Values extends FormikValues> {
  title: React.ReactNode
  initialValues: Values
  save: (values: Values) => Promise<SaveOutcome>
  children: React.ReactNode
  validationSchema?: AnyObjectSchema
}

const CatalogueItemForm = <Values extends FormikValues>({
  title,
  initialValues,
  save,
  children,
  validationSchema
}: CatalogueItemFormProps<Values>) => {
  const { meta } = useCatalogue()

  const submit = async (values: Values, helpers: FormikHelpers<Values>) => {
    const outcome = await save(values)
    if (outcome.ok) {
      meta.go.browse()
      return
    }
    helpers.setStatus({ errors: outcome.errors })
  }

  return (
    <div className="inline-edit-form">
      <h3>{title}</h3>
      <div className="pb-2 modal-form catalogue-form">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
          <>
            {children}
            <SubmittingIndicator />
            <UnsavedChangesGuard />
          </>
        </Formik>
      </div>
    </div>
  )
}

export default CatalogueItemForm
